import { ROUTES, USER_ROLES } from './constants';

export type UserRole =
  | typeof USER_ROLES.DEV_TEAM
  | typeof USER_ROLES.SCHOOL_ADMIN
  | typeof USER_ROLES.TEACHER
  | typeof USER_ROLES.STUDENT
  | typeof USER_ROLES.PARENT;

/** Allow only same-app relative paths (open redirect protection). */
export function sanitizeReturnTo(returnTo: string | null | undefined): string | null {
  if (!returnTo || typeof returnTo !== 'string') return null;
  const trimmed = returnTo.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  if (/^https?:\/\//i.test(trimmed)) return null;
  return trimmed;
}

/** Build a safe in-app return path from router location (pathname + search). */
export function locationToReturnPath(pathname: string, search?: string | null): string | null {
  const path = `${pathname || ''}${search || ''}`;
  return sanitizeReturnTo(path);
}

export const STRIPE_CHECKOUT_RETURN_KEY = 'tkm_stripe_checkout_return';

export function saveStripeCheckoutReturnPath(path: string): void {
  if (typeof sessionStorage === 'undefined') return;
  const safe = sanitizeReturnTo(path);
  if (safe) sessionStorage.setItem(STRIPE_CHECKOUT_RETURN_KEY, safe);
}

export function consumeStripeCheckoutReturnPath(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(STRIPE_CHECKOUT_RETURN_KEY);
  sessionStorage.removeItem(STRIPE_CHECKOUT_RETURN_KEY);
  return sanitizeReturnTo(raw);
}

export function getOnboardingRouteForRoles(roles: string[] | undefined | null): string | null {
  if (!roles?.length) return null;
  if (roles.includes(USER_ROLES.SCHOOL_ADMIN)) return ROUTES.ONBOARDING_SCHOOL_SETUP;
  if (roles.includes(USER_ROLES.TEACHER)) return ROUTES.ONBOARDING_TEACHER_PROFILE;
  if (roles.includes(USER_ROLES.STUDENT)) return ROUTES.ONBOARDING_STUDENT_PROFILE;
  return null;
}

export function getDefaultRouteForRoles(
  roles: string[] | undefined | null,
  onboardingComplete?: boolean
): string {
  if (!roles?.length) return ROUTES.LOGIN;
  if (onboardingComplete === false) {
    const onboardingRoute = getOnboardingRouteForRoles(roles);
    if (onboardingRoute) return onboardingRoute;
  }
  if (roles.includes(USER_ROLES.DEV_TEAM)) return ROUTES.DEV_DASHBOARD;
  if (roles.includes(USER_ROLES.SCHOOL_ADMIN)) return ROUTES.ADMIN_DASHBOARD;
  if (roles.includes(USER_ROLES.TEACHER)) return ROUTES.TEACHER_DASHBOARD;
  if (roles.includes(USER_ROLES.STUDENT)) return ROUTES.STUDENT_DASHBOARD;
  if (roles.includes(USER_ROLES.PARENT)) return ROUTES.PARENT_DASHBOARD;
  return ROUTES.LOGIN;
}

/** Admin login shells map to these path prefixes for safe returnTo handling. */
const ADMIN_ROLE_PATH_PREFIX: Partial<Record<string, string>> = {
  [USER_ROLES.DEV_TEAM]: '/dev',
  [USER_ROLES.SCHOOL_ADMIN]: '/admin',
};

export interface ResolveAdminPostLoginPathOptions {
  /** Platform role that was authenticated (expected_role after successful login). */
  authenticatedRole: string;
  returnTo?: string | null;
}

/**
 * Admin login redirect: SCHOOL_ADMIN before DEV_TEAM; honors role-scoped returnTo.
 */
export function resolveAdminPostLoginPathFromRoles(
  roles: string[] | null | undefined,
  returnTo?: string | null
): string {
  const normalized = (roles ?? []).map((r) => String(r).toUpperCase());
  let authenticatedRole: string | null = null;
  if (normalized.includes(USER_ROLES.SCHOOL_ADMIN)) {
    authenticatedRole = USER_ROLES.SCHOOL_ADMIN;
  } else if (normalized.includes(USER_ROLES.DEV_TEAM)) {
    authenticatedRole = USER_ROLES.DEV_TEAM;
  }
  if (!authenticatedRole) return ROUTES.LOGIN;
  return resolveAdminPostLoginPath({ authenticatedRole, returnTo });
}

/** Primary admin role for headers/storage after admin login (School Admin wins over Dev Team). */
export function resolvePrimaryAdminRole(roles: string[] | null | undefined): string | null {
  const normalized = (roles ?? []).map((r) => String(r).toUpperCase());
  if (normalized.includes(USER_ROLES.SCHOOL_ADMIN)) return USER_ROLES.SCHOOL_ADMIN;
  if (normalized.includes(USER_ROLES.DEV_TEAM)) return USER_ROLES.DEV_TEAM;
  return null;
}

/**
 * Admin login redirect for a known authenticated admin role.
 * Honors returnTo only when it is a safe in-app path under that role's prefix.
 */
export function resolveAdminPostLoginPath(options: ResolveAdminPostLoginPathOptions): string {
  const role = options.authenticatedRole;
  const defaultDashboard =
    role === USER_ROLES.DEV_TEAM
      ? ROUTES.DEV_DASHBOARD
      : role === USER_ROLES.SCHOOL_ADMIN
        ? ROUTES.ADMIN_DASHBOARD
        : getDefaultRouteForRoles([role as UserRole]);

  const prefix = ADMIN_ROLE_PATH_PREFIX[role];
  const safeReturnTo = sanitizeReturnTo(options.returnTo);
  if (safeReturnTo && prefix && safeReturnTo.startsWith(prefix)) {
    return safeReturnTo;
  }
  return defaultDashboard;
}

export interface ResolvePostLoginPathOptions {
  returnTo?: string | null;
  fromPathname?: string | null;
  roles?: string[] | null;
  onboardingComplete?: boolean;
}

/**
 * Post-login redirect priority:
 * 1. ?returnTo= (sanitized)
 * 2. location.state.from.pathname (RequireAuth)
 * 3. Role-based dashboard
 */
export function resolvePostLoginPath(options: ResolvePostLoginPathOptions): string {
  const stripeReturn = consumeStripeCheckoutReturnPath();
  if (stripeReturn) return stripeReturn;

  if (options.onboardingComplete === false) {
    const onboardingRoute = getOnboardingRouteForRoles(options.roles);
    if (onboardingRoute) return onboardingRoute;
  }

  const safeReturnTo = sanitizeReturnTo(options.returnTo);
  if (safeReturnTo) return safeReturnTo;

  const from = sanitizeReturnTo(options.fromPathname ?? null);
  if (from) return from;

  return getDefaultRouteForRoles(options.roles, options.onboardingComplete);
}
